// Shared types, date helpers, formatters and small components for the Statistics area.
// Phase 1 extraction from the original single StatisticsPage.tsx — behaviour unchanged.
import React from 'react';

export type DatePreset = 'last30' | 'week' | 'month' | 'all' | 'custom';

/** Every statistics page offers the same presets (Fernando, 2026-09-16: "All time" too). */
export const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'last30', label: 'Last 30 days' },
  { key: 'week',   label: 'This week'    },
  { key: 'month',  label: 'This month'   },
  { key: 'all',    label: 'All time'     },
  { key: 'custom', label: 'Custom'       },
];

/** "All time": from before the WMS existed to today — the backend has no range cap. */
export function allTimeRange() {
  return { from: '2020-01-01', to: new Date().toISOString().slice(0, 10) };
}

export interface Session {
  username: string;
  display_name: string;
  date: string;
  round_start: string | null;
  round_end: string | null;
  orders: number;
  solving_orders: number;
  /** How many of this round's orders have reached Archived. A round gets no end
   *  time until at least half are processed, so this is what distinguishes
   *  "still packing" from "packed but never processed". */
  archived_orders?: number;
  duration_minutes: number;
  orders_per_hour: number | null;
  /** OPEN round only (server time of the response): the clock so far, the speed if the round
   *  ended now (every counted order over that clock) and the pace of what is processed so far.
   *  The table ticks the clock on from the moment the data arrived. */
  live_elapsed_minutes?: number | null;
  live_orders_per_hour?: number | null;
  live_processed_per_hour?: number | null;
  /** 'tracked' = a real round from pp_tracking (2026-09-07 on): started by the
   *  Printed move, ended by the last Process, every step recorded.
   *  'estimated' = the old printed_at clustering, shown only for days that have
   *  no tracked round. */
  source?: 'tracked' | 'estimated';
  round_id?: number;
  flow_key?: string;
  status?: string;
  starter?: string | null;
  picker?: string | null;
  picker_device?: string | null;
  packers?: string | null;
  picking_minutes?: number | null;
  packing_minutes?: number | null;
  /** share of the whole round, 0–100 */
  picking_pct?: number | null;
  packing_pct?: number | null;
  /** diverted = left for Label issue / Hard to find / Can't find — still counted
   *  (the time was spent). excluded = moved back to New etc. — not part of the round. */
  diverted?: number;
  excluded?: number;
  diverted_to?: string | null;
  auto_closed?: boolean;
  /** metres walked, from the recorded stops on the warehouse plan (Phase 2) */
  metres?: number | null;
  metres_per_order?: number | null;
  /** of the metres: the extra walking of the box swaps (to the station and back) */
  metres_swaps?: number | null;
  /** box swaps: a Continue for trip 2+; minutes = swap screen → Continue (timed ones only) */
  swaps?: number;
  swaps_timed?: number;
  swap_minutes?: number | null;
  /** where the picking time went (pp_pace): walking at the person's speed, at the shelves, box
   *  changes, Back re-checks, the rest; pace_source = 'own' | 'warehouse' */
  walk_minutes?: number | null;
  shelf_minutes?: number | null;
  box_change_minutes?: number | null;
  check_minutes?: number | null;
  other_minutes?: number | null;
  pace_source?: string | null;
  walking_speed?: number | null;
  tapped_through?: boolean;
  packed_orders?: number;
  /** real barcode scans that matched (manual and refused ones NOT included, 2026-09-28) */
  item_scans?: number;
  pieces?: number;
  pick_items_per_hour?: number | null;
  pack_items_per_hour?: number | null;
  /** pieces packed with the "Pack" button, no barcode */
  manual_packs?: number;
  scan_fails?: number;
  packers_detail?: { packer: string; scans: number; manual: number }[];
  pick_steps?: number;
}

export interface DailyRow {
  date: string;
  username: string;
  display_name: string;
  pp_count: number;
  processed_count: number;
  problem_count: number;
}

export interface ProblematicOrder {
  order_id: string;
  external_id: number;
  customer_name: string;
  tab: string;
  problem_tab: string;
  order_date: string | null;
  moved_at: string | null;
  display_name: string;
}

export function weekRange() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const mon = new Date(now); mon.setDate(now.getDate() + diff);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { from: mon.toISOString().slice(0, 10), to: sun.toISOString().slice(0, 10) };
}

export function monthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last  = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

export function last30Range() {
  const to   = new Date();
  const from = new Date(); from.setDate(to.getDate() - 29);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function speedColor(oph: number | null) {
  if (oph === null) return 'text-gray-500';
  if (oph >= 30)   return 'text-green-400';
  if (oph >= 20)   return 'text-yellow-400';
  return 'text-red-400';
}

export function speedRowBg(oph: number | null) {
  if (oph === null) return '';
  if (oph >= 30)   return 'bg-green-950/30';
  if (oph >= 20)   return 'bg-yellow-950/30';
  return 'bg-red-950/30';
}

export function fmtTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
}

export function fmtDur(mins: number | null | undefined) {
  if (mins == null) return '—';
  if (mins < 1) return '<1 min';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${m}m`;
}

export function KPICard({ label, value, sub, color }: {
  label: string; value: string; sub?: string; color?: 'green' | 'yellow' | 'red';
}) {
  const vc = color === 'green' ? 'text-green-400' : color === 'yellow' ? 'text-yellow-400' : color === 'red' ? 'text-red-400' : 'text-white';
  return (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
      <p className="text-xs text-gray-500 mb-1.5 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold ${vc}`}>{value}</p>
      {sub && <p className="text-xs text-gray-600 mt-1">{sub}</p>}
    </div>
  );
}
